import type { TimelineDateBasis } from "./timeline-types";
import type { DocumentRowMapped } from "./types";

/**
 * The date columns, in the order the product means them, or `null` for a row
 * carrying none of them (row-shape drift between an RPC and the selects) or an
 * unparseable one.
 */
function readDocDate(doc: DocumentRowMapped, basis: TimelineDateBasis): Date | null {
  const raw =
    basis === "upload"
      ? doc.created_at
      : doc.effective_modified_at ||
        doc.document_content_modified_date ||
        doc.original_created_at ||
        doc.created_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

// One warning per document, not one per render: getDocTimestamp is called from
// row renderers and comparators. Bounded so a tab left open on a building whose
// rows are all dateless cannot grow it without limit — and so a row whose date
// is later fixed and broken again still gets a second warning. A dateless row
// is row-shape drift, so in practice this holds nothing at all.
const WARNED_DATELESS_LIMIT = 500;
const warnedDatelessIds = new Set<DocumentRowMapped["id"]>();

/**
 * The date the product means by "when did this document last change" — the one
 * the "File modified" column shows, the list sorts on, and the timeline places
 * rows by on its default (`file`) basis. On the `upload` basis it is
 * `created_at`, the column the timeline RPCs order on when asked for it.
 *
 * Reads `effective_modified_at`, the STORED generated column Postgres
 * materialises as `COALESCE(document_content_modified_date,
 * original_created_at, created_at)`, and falls back to that same chain in JS
 * for rows fetched by a select that did not ask for it. Never re-derive the
 * COALESCE at a call site: the server-side copy of it drifted once already
 * (the timeline RPCs ordered on `COALESCE(original_created_at, created_at)`
 * until franklin-data-pipeline 20260831144525, which parked a re-uploaded
 * Office file at the creation date embedded in the file itself — weeks before
 * the version that produced the row).
 */
export function getDocTimestamp(doc: DocumentRowMapped, basis: TimelineDateBasis = "file"): Date {
  const date = readDocDate(doc, basis);
  if (date) return date;

  // A row carrying none of the four must not hand callers an Invalid Date:
  // `format()` throws a RangeError on one, which takes down the whole card
  // rather than one field, and `getTime()` is NaN, which makes every comparator
  // built on it order arbitrarily. The epoch is a wrong date; it is not a broken
  // timeline — but it is still a wrong date that reads as real data, so say so
  // once, and use `hasDocTimestamp` wherever one row can skew a whole range.
  if (!warnedDatelessIds.has(doc.id)) {
    if (warnedDatelessIds.size >= WARNED_DATELESS_LIMIT) warnedDatelessIds.clear();
    warnedDatelessIds.add(doc.id);
    console.warn(
      `Document ${doc.id} carries no date column (effective_modified_at, document_content_modified_date, original_created_at, created_at); placing it at the epoch`
    );
  }
  return new Date(0);
}

/** False for a row `getDocTimestamp` can only answer with the epoch. */
export function hasDocTimestamp(
  doc: DocumentRowMapped,
  basis: TimelineDateBasis = "file"
): boolean {
  return readDocDate(doc, basis) !== null;
}

/**
 * The server's own row order, newest first — the comparator every client-side
 * merge of server rows must use.
 *
 * `get_documents_in_time_window` pages `ORDER BY <basis key> DESC, id DESC`
 * (`effective_modified_at`, or `created_at` on the upload basis), and its
 * cursor is that whole pair. Sorting a merge on the timestamp
 * alone leaves rows sharing an instant in whatever order the merge happened to
 * produce, so a cluster renders them in one order and pages them in another:
 * the row above the page boundary on screen is not the row the next page
 * continues from, and re-reading the window shuffles them again.
 */
export function compareDocsNewestFirst(
  a: DocumentRowMapped,
  b: DocumentRowMapped,
  basis: TimelineDateBasis = "file"
): number {
  const byTime = getDocTimestamp(b, basis).getTime() - getDocTimestamp(a, basis).getTime();
  return byTime !== 0 ? byTime : b.id - a.id;
}

/** `compareDocsNewestFirst` bound to one basis, for `Array.prototype.sort`. */
export function docsNewestFirst(
  basis: TimelineDateBasis
): (a: DocumentRowMapped, b: DocumentRowMapped) => number {
  return (a, b) => compareDocsNewestFirst(a, b, basis);
}
